import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

const BrainVisualization = ({ scrollProgress, currentSlide }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const cameraRef = useRef(null);
  const cortexPointsRef = useRef(null);
  const corePointsRef = useRef(null);
  const connectionsRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const animationFrameRef = useRef(null);

  // Store current props in refs so animation loop can access them
  const scrollProgressRef = useRef(scrollProgress);
  const currentSlideRef = useRef(currentSlide);
  const visualScrollProgressRef = useRef(0);

  // Update refs when props change
  useEffect(() => {
    scrollProgressRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000); // Force black background
    scene.fog = new THREE.FogExp2(0x000000, 0.03);
    sceneRef.current = scene;

    // Camera (Cinematic Wide)
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 0, 40);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: false, 
      powerPreference: "high-performance"
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 1);
    renderer.toneMapping = THREE.ReinhardToneMapping;
    // renderer.outputColorSpace is SRGBColorSpace by default in r152+ (v0.181 installed)
    rendererRef.current = renderer;

    mountRef.current.appendChild(renderer.domElement);

    // Post Processing (Bloom removed/minimized for clarity)
    const renderScene = new RenderPass(scene, camera);
    // Only very minimal bloom for "light" feel, effectively sharp
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.5, 0.4, 0.85);
    bloomPass.threshold = 0.8; // Only brightest pixels glow
    bloomPass.strength = 0.3;  // Very weak
    bloomPass.radius = 0.1;    // Almost no blur radius

    const composer = new EffectComposer(renderer);
    composer.addPass(renderScene);
    composer.addPass(bloomPass);
    composerRef.current = composer;

    // Build Objects
    buildBrain();

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      const time = clockRef.current.getDelta();
      if (cortexPointsRef.current) {
        cortexPointsRef.current.material.uniforms.time.value += time;
      }

      // Inertial Scroll Smoothing
      const target = scrollProgressRef.current;
      const current = visualScrollProgressRef.current;
      
      // Smoothly interpolate towards target
      // visualScrollProgressRef.current += (target - current) * 0.08;
      visualScrollProgressRef.current = current + (target - current) * 0.08;

      updateCamera();

      // Slow idle rotation
      scene.rotation.y += 0.001;

      composer.render();
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      composer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  const buildBrain = () => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;

    // V1 Style but refined
    const cortexGeo = new THREE.BufferGeometry();
    const coreGeo = new THREE.BufferGeometry();

    const cortexPos = [], cortexCol = [], cortexSize = [];
    const corePos = [], coreCol = [];

    // Cortex (Outer)
    for (let i = 0; i < 4000; i++) {
      const u = Math.random() * Math.PI;
      const v = Math.random() * Math.PI * 2;
      const r = 12 + (Math.random() - 0.5) * 2; // Tight band

      // Brain Shape Mod
      const x = Math.sin(u) * Math.cos(v) * r;
      const z = Math.sin(u) * Math.sin(v) * r;
      const y = Math.cos(u) * r * 0.8;

      // Gap
      const finalX = x + (x > 0 ? 0.5 : -0.5);

      cortexPos.push(finalX, y, z);

      // Timelit Palette: Cyan to Purple gradient
      // HSL: Cyan (0.5) -> Purple (0.75)
      const hue = 0.5 + Math.random() * 0.25;
      const col = new THREE.Color().setHSL(hue, 0.9, 0.6);
      cortexCol.push(col.r, col.g, col.b);
      cortexSize.push(Math.random());
    }

    // Core (Inner)
    for (let i = 0; i < 1500; i++) {
      const u = Math.random() * Math.PI;
      const v = Math.random() * Math.PI * 2;
      const r = 4 + Math.random() * 2;

      const x = Math.sin(u) * Math.cos(v) * r;
      const z = Math.sin(u) * Math.sin(v) * r;
      const y = Math.cos(u) * r * 0.7;

      corePos.push(x, y, z);

      // Deep Violet/Indigo for Core
      const col = new THREE.Color().setHSL(0.7 + Math.random() * 0.1, 0.8, 0.4);
      coreCol.push(col.r, col.g, col.b);
    }

    // Geometry Attrs
    cortexGeo.setAttribute('position', new THREE.Float32BufferAttribute(cortexPos, 3));
    cortexGeo.setAttribute('color', new THREE.Float32BufferAttribute(cortexCol, 3));
    cortexGeo.setAttribute('size', new THREE.Float32BufferAttribute(cortexSize, 1));

    coreGeo.setAttribute('position', new THREE.Float32BufferAttribute(corePos, 3));
    coreGeo.setAttribute('color', new THREE.Float32BufferAttribute(coreCol, 3));

    // Materials (Custom Point Shader)
    const material = new THREE.ShaderMaterial({
      uniforms: {
        pixelRatio: { value: renderer.getPixelRatio() },
        time: { value: 0 }
      },
      vertexShader: `
        uniform float pixelRatio;
        uniform float time;
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec3 pos = position;
          // Breathing
          pos *= 1.0 + sin(time * 2.0 + position.x) * 0.02;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = (size * 2.0 + 1.0) * pixelRatio * (50.0 / -mv.z);
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float r = length(gl_PointCoord - vec2(0.5));
          if(r > 0.5) discard;
          float glow = 1.0 - (r * 2.0);
          glow = pow(glow, 2.0);
          gl_FragColor = vec4(vColor, glow);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const cortexPoints = new THREE.Points(cortexGeo, material);
    const corePoints = new THREE.Points(coreGeo, material.clone());

    cortexPointsRef.current = cortexPoints;
    corePointsRef.current = corePoints;

    scene.add(cortexPoints);
    scene.add(corePoints);

    // Connections (Lines) - Initially hidden
    const lineGeo = new THREE.BufferGeometry();
    const linePos = [];
    // Generate random connections
    for (let i = 0; i < 200; i++) {
      const idxA = Math.floor(Math.random() * 1500) * 3;
      const idxB = Math.floor(Math.random() * 4000) * 3;

      linePos.push(
        corePos[idxA], corePos[idxA + 1], corePos[idxA + 2],
        cortexPos[idxB], cortexPos[idxB + 1], cortexPos[idxB + 2]
      );
    }
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePos, 3));
    const lineMat = new THREE.LineBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
    const connections = new THREE.LineSegments(lineGeo, lineMat);
    connectionsRef.current = connections;
    scene.add(connections);
    console.log('Neural connections created:', connections);
  };

  const updateCamera = () => {
    const camera = cameraRef.current;
    const connections = connectionsRef.current;
    // Use the smoothed visual progress
    const t = visualScrollProgressRef.current;

    const isMobile = window.innerWidth < 768;
    const distMult = isMobile ? 1.5 : 1.0;
    const heightMult = isMobile ? 0.8 : 1.0;

    // Simple Rail (Spline-like behavior using Math)
    const x = Math.sin(t * Math.PI * 2) * 30 * distMult;
    const z = (Math.cos(t * Math.PI * 2) * 30 + 10) * distMult;
    const y = Math.sin(t * Math.PI) * 10 * heightMult;

    // Smooth damp
    camera.position.x += (x - camera.position.x) * 0.05;
    camera.position.y += (y - camera.position.y) * 0.05;
    camera.position.z += (z - camera.position.z) * 0.05;

    camera.lookAt(0, 0, 0);

    // Interactive Elements based on Slide
    // Slide 3 (Contextual Recall) -> Show Lines
    if (connections && connections.material) {
      // Use ref to get fresh currentSlide value
      if (currentSlideRef.current === 3) {
        connections.material.opacity += (0.4 - connections.material.opacity) * 0.05;
      } else {
        connections.material.opacity += (0 - connections.material.opacity) * 0.1;
      }
    }
  };

  return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
};

export default BrainVisualization;
